import React, { useState, useEffect, useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { LeagueContext } from '../../Context'
import { Box } from '../../Components/Layout'
import { PlayerTransactions } from '../../Api'
import { Spinner } from '../../Components/Icons'
import { colors } from '../../Components/consts'
import { YearSelect } from '../../Components/Layout/Selectors/Year/YearSelect'
import { TeamSelect } from '../../Components/Layout/Selectors/Team/TeamSelect'
import { TypeSelect } from '../../Components/Layout/Selectors/Type/TypeSelect'
import { InnerCon, ResetBtn } from './styles'
import { Con } from '../../Components/Layout/Containers/ConStyles'
import { AdUnit } from '../../Components/Ads/AdUnit'
import { TRANSACTIONS_AD_INSERT_NUMBER, capitalize, shouldShowAd } from '../../Utils'
import { SEASON_SELECTOR_TYPES } from '../../Constants/season'
import { TableHeader } from '../../Components/Tables/TableHeader'

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const TransactionsTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  let query = useQuery()
  const { playerTransactions, fetchPlayerTransaction } = useContext(LeagueContext)!
  const [transactions, setTransactions] = useState<PlayerTransactions[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [team, setTeam] = useState<string>('All Teams')
  const [year, setYear] = useState<number>(0)
  const [transactionType, setTransactionType] = useState<string>('All')

  useEffect(() => {
    const teamQuery = query.get('team')
    if (teamQuery) {
      //Capitalize first letter of team
      const tm = capitalize(teamQuery)
      setTeam(tm)
    }
    if (playerTransactions.length > 0) {
      filterTransactions()
      setLoading(false)
      return
    }
    getTransactions()
  }, [playerTransactions])

  useEffect(() => {
    filterTransactions()
  }, [team, year, transactionType])

  const getTransactions = async () => {
    const t = await fetchPlayerTransaction()
    setTransactions(t)
    setLoading(false)
    return
  }

  const onChangeTeam = (tm: string) => {
    setTeam(tm)
  }

  const onChangeYear = (yr: number) => {
    setYear(yr)
  }

  const onChangeType = (typ: string) => {
    setTransactionType(typ)
  }

  const filterTransactions = () => {
    const yrStr = year.toString()
    const filtered = playerTransactions.filter(
      (t) =>
        (team === 'All Teams'
          ? t.moveFrom
          : t.moveFrom.toLowerCase() === team.toLowerCase() ||
            t.moveTo.toLowerCase() === team.toLowerCase()) &&
        (year === 0 ? t.date : t.date.startsWith(yrStr)) &&
        (transactionType === 'All'
          ? t.moveFrom
          : t.moveFrom.toLowerCase() === transactionType.toLowerCase() ||
            t.moveTo.toLowerCase() === transactionType.toLowerCase())
    )
    setTransactions(filtered)
  }

  const clearFilters = () => {
    setTeam('All Teams')
    setYear(0)
    setTransactionType('All')
    setTransactions(playerTransactions)
  }

  return (
    <Box noMargin padding={0}>
      <InnerCon>
        <Con noMargin noPadding>
          {loading && (
            <div>
              <Spinner fill={colors.yellow} />
              Loading...
            </div>
          )}
          <div className="tableCon">
            <div className="selectorCon">
              <YearSelect
                current={year}
                onChangeYear={onChangeYear}
                requireAllYear={true}
                seasonSelectorType={SEASON_SELECTOR_TYPES.MAIN}
              />
              <TeamSelect current={team} onChangeTeam={onChangeTeam} />
              <TypeSelect current={transactionType} onChangeType={onChangeType} />
              <ResetBtn onClick={clearFilters}>Reset</ResetBtn>
            </div>
            {!loading && transactions.length > 0 && (
              <>
                <div className="tableOuter">
                  <table className="table">
                    <thead>
                      <tr>
                        <TableHeader ariaLabel="Date">Date</TableHeader>
                        <TableHeader ariaLabel="Player">Player</TableHeader>
                        <TableHeader ariaLabel="Position">Position</TableHeader>
                        <TableHeader ariaLabel="From">From</TableHeader>
                        <TableHeader ariaLabel="To">To</TableHeader>
                        <TableHeader ariaLabel="Details">Details</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((row, idx) => (
                        <React.Fragment key={`${row.officialId}${row.date}${row.id}`}>
                          <tr>
                            <td>{row.date}</td>
                            <td>
                              <Con noMargin noPadding align="center">
                                <span>{row.playerName}</span>
                              </Con>
                            </td>
                            <td>{row.position}</td>
                            <td>{row.moveFrom}</td>
                            <td>{row.moveTo}</td>
                            <td>{row.details}</td>
                          </tr>
                          {shouldShowAd(idx, TRANSACTIONS_AD_INSERT_NUMBER) && (
                            <tr>
                              <td colSpan={6} align="center">
                                <AdUnit inContent={true} bannerType="bannerInContent" aria-label="Transactions table ad" />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </Con>
      </InnerCon>
    </Box>
  )
}
